import { default as React } from 'react';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  useApi,
  configApiRef,
  alertApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    minWidth: '80vw',
    maxWidth: '90vw',
    minHeight: '70vh',
    maxHeight: '85vh',
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  titleIcon: {
    color: theme.palette.primary.main,
  },
  closeButton: {
    marginLeft: theme.spacing(2),
  },
  dialogContent: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  logsContainer: {
    flex: 1,
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
    fontSize: '12px',
    lineHeight: '1.5',
    padding: theme.spacing(2),
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    minHeight: '300px',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    minHeight: '300px',
    color: theme.palette.text.secondary,
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    justifyContent: 'space-between',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  podInfo: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  refreshButton: {
    textTransform: 'none',
  },
}));

interface PodLogsModalProps {
  open: boolean;
  onClose: () => void;
  appName: string;
  argoCdAppName: string;
  namespace: string;
}

interface LogsResponse {
  logs: string;
  podName?: string;
  cached?: boolean;
  cachedAt?: string;
}

export const PodLogsModal = ({
  open,
  onClose,
  appName,
  argoCdAppName,
  namespace,
}: PodLogsModalProps) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const alertApi = useApi(alertApiRef);
  const identityApi = useApi(identityApiRef);

  const [logs, setLogs] = useState<string>('');
  const [podName, setPodName] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);
  const logsContainerRef = useRef<HTMLDivElement>(null);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const backendUrl = configApi.getString('backend.baseUrl');
      // Pass entity name in URL path for catalog lookup, ArgoCD app name as query param for logs API
      const apiUrl = `${backendUrl}/api/microapps-api/pod-logs/${encodeURIComponent(
        appName,
      )}?namespace=${encodeURIComponent(
        namespace,
      )}&argoCdAppName=${encodeURIComponent(argoCdAppName)}&tailLines=1000`;

      // Get authentication token
      const { token } = await identityApi.getCredentials();

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(
          result.error ||
            result.message ||
            `Failed to fetch logs (${response.status})`,
        );
      }

      const result: LogsResponse = await response.json();
      setLogs(result.logs || '');
      setPodName(result.podName);
      setIsCached(result.cached || false);

      // Auto-scroll to bottom when logs load
      setTimeout(() => {
        if (logsContainerRef.current) {
          logsContainerRef.current.scrollTop =
            logsContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      alertApi.post({
        message: `Failed to fetch pod logs: ${errorMessage}`,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [appName, namespace, argoCdAppName, configApi, identityApi, alertApi]);

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
    // Reset state when modal closes
    return () => {
      if (!open) {
        setLogs('');
        setPodName(undefined);
        setError(null);
        setIsCached(false);
      }
    };
  }, [open, fetchLogs]);

  const handleRefresh = () => {
    fetchLogs();
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="pod-logs-dialog-title"
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle id="pod-logs-dialog-title" className={classes.dialogTitle}>
        <div className={classes.titleContent}>
          <DescriptionIcon className={classes.titleIcon} />
          <Typography variant="h6" component="span">
            Pod Logs: {appName}
          </Typography>
        </div>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className={classes.closeButton}
          disabled={isLoading}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {isLoading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={48} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Fetching pod logs...
            </Typography>
          </Box>
        )}

        {!isLoading && error && (
          <Box className={classes.emptyContainer}>
            <Alert severity="error" sx={{ mb: 2, maxWidth: '600px' }}>
              {error}
            </Alert>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              className={classes.refreshButton}
            >
              Retry
            </Button>
          </Box>
        )}

        {!isLoading && !error && !logs && (
          <Box className={classes.emptyContainer}>
            <Typography variant="body1" color="textSecondary">
              No logs available for this application.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              The pod may not be running or may not have generated any logs yet.
            </Typography>
          </Box>
        )}

        {!isLoading && !error && logs && (
          <Box className={classes.logsContainer} ref={logsContainerRef}>
            {logs}
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Box className={classes.podInfo}>
          {podName && <span>Pod: {podName}</span>}
          {isCached && <span> (cached)</span>}
        </Box>
        <Box className={classes.actionButtons}>
          <Button
            onClick={handleRefresh}
            startIcon={
              isLoading ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            disabled={isLoading}
            variant="outlined"
            className={classes.refreshButton}
          >
            Refresh
          </Button>
          <Button
            onClick={handleClose}
            color="primary"
            variant="contained"
            disabled={isLoading}
          >
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
